import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Upload, Check, Mail, Phone } from "lucide-react";

const LOGO = "/assets/logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirrors the approved fixed prices on the live site (server recomputes these)
const FIXED_PRICES = {
  "Essential Logo": { label: "Essential Logo (1 text-based concept, 1 revision)", price: 45 },
  "Starter Logo": { label: "Starter Logo (concept with text and simple icon, 2 revisions)", price: 85 },
  "Signature Logo": { label: "Signature Logo (2 custom concepts, 2 revisions)", price: 150 },
  "Premium Logo": { label: "Premium Logo (2 detailed concepts, 3 revisions, alternate version)", price: 250 },
  "Luxury Logo Collection": { label: "Luxury Logo Collection (3 concepts, 4 revisions, variations + brand palette)", price: 450 },
  "Brand Kit": { label: "Brand Kit Add-On", price: 150 },
  "3D Logo Upgrade": { label: "3D Logo Upgrade", price: 125 },
  "Business Cards": { label: "Business Cards (two-sided)", price: 100 },
  "Flyer": { label: "Flyer (one-sided)", price: 125 },
  "Price Sheet / Menu": { label: "Price Sheet / Menu", price: 125 },
  "Tri-Fold Brochure": { label: "Tri-Fold Brochure", price: 200 },
  "Business Documents": { label: "Business Documents (invoice/form template)", price: 100 },
  "Bronze Brand Starter": { label: "BRONZE Brand Starter Package", price: 500 },
  "Silver Brand Builder": { label: "SILVER Brand Builder Package", price: 1000 },
  "Gold Business Launch": { label: "GOLD Business Launch Package", price: 1800 },
};
const MONTHLY_SERVICE = "Monthly Social Media Management";

const SERVICE_OPTIONS = [
  "Essential Logo", "Starter Logo", "Signature Logo", "Premium Logo", "Luxury Logo Collection",
  "Brand Kit", "3D Logo Upgrade", "Business Cards", "Flyer",
  "Price Sheet / Menu", "Tri-Fold Brochure", "Business Documents",
  "Social Media Graphics", "Promotional Video", "Website Refresh",
  "Full Website Refresh", "New Website", "Grant Support",
  "Bronze Brand Starter", "Silver Brand Builder", "Gold Business Launch",
  MONTHLY_SERVICE, "Other",
];

const DETAIL_FIELDS = [
  { key: "create_description", label: "What would you like us to create?", required: true },
  { key: "about_business", label: "Tell us about your business." },
  { key: "purpose", label: "What is the purpose of this project?" },
  { key: "audience", label: "Who is your target audience?" },
  { key: "wording", label: "What exact wording or information needs to be included?" },
  { key: "colors", label: "What colors would you like used?" },
  { key: "avoid", label: "Are there colors, styles, fonts, or design elements you do NOT want?" },
  { key: "look_feel", label: "Describe the overall look or feeling you want." },
];

const TERMS = [
  "I have reviewed my project information and provided the files and links currently available.",
  "I understand that up to 2 revision rounds are included unless otherwise stated, and additional rounds are $50 each.",
  "I understand additional work outside the selected service may require an additional fee.",
  "I understand turnaround time begins after required project information and required payment/deposit are received.",
  "I agree to the project and payment terms.",
];

const STEPS = [
  { n: 1, label: "Your Info", target: "syp-info" },
  { n: 2, label: "Your Project", target: "syp-services" },
  { n: 3, label: "Files & Links", target: "syp-files" },
  { n: 4, label: "Review", target: "syp-review" },
  { n: 5, label: "Payment", target: "syp-payment" },
];

const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
const fmt = (n) => `$${Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2)}`;

const inputCls = "w-full bg-white border border-copper/25 rounded-[10px] px-4 py-3.5 text-ink-900 placeholder-ink-800/40 focus:border-copper-500 focus:outline-none";
const labelCls = "block font-bold text-sm text-ink-900 mb-1.5";
const helpCls = "text-sm text-ink-800/60 mb-2";

function SectionHead({ n, title, heading, dark = false }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-4 mb-5">
        <span className="w-11 h-11 rounded-full border border-copper-500/70 text-copper-500 flex items-center justify-center font-serif text-lg shrink-0">{n}</span>
        <p className="text-[11px] uppercase tracking-[0.35em] font-semibold text-copper-600">{title}</p>
      </div>
      <h2 className={`font-serif font-medium text-3xl md:text-4xl leading-tight ${dark ? "text-white" : "text-ink-900"}`}>{heading}</h2>
    </div>
  );
}

export default function StartProjectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInput = useRef(null);

  const [info, setInfo] = useState({ full_name: "", business_name: "", email: "", phone: "", website: "", social_links: "" });
  const [services, setServices] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [details, setDetails] = useState({ create_description: "", about_business: "", purpose: "", audience: "", wording: "", colors: "", avoid: "", look_feel: "", completion_date: "", anything_else: "" });
  const [files, setFiles] = useState([]);
  const [workLinks, setWorkLinks] = useState([""]);
  const [inspLinks, setInspLinks] = useState([""]);
  const [inspNotes, setInspNotes] = useState("");
  const [payment, setPayment] = useState("deposit");
  const [terms, setTerms] = useState(TERMS.map(() => false));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  // payment return flow: null | "processing" | "paid" | "cancelled"
  const [payState, setPayState] = useState(null);
  const [pendingProject, setPendingProject] = useState(null);
  const [payError, setPayError] = useState("");

  // Handle return from Stripe Checkout
  useEffect(() => {
    const p = searchParams.get("payment");
    if (!p) return;
    if (p === "cancelled") {
      setPendingProject(searchParams.get("project"));
      setPayState("cancelled");
      setDone(true);
      return;
    }
    if (p === "success") {
      const sid = searchParams.get("session_id");
      if (!sid) return;
      setPayState("processing");
      let tries = 0;
      const poll = async () => {
        try {
          const r = await fetch(`${API}/payments/status/${sid}`);
          const d = await r.json();
          if (d.payment_status === "paid") {
            setPayState("paid");
            setDone(true);
            window.scrollTo({ top: 0, behavior: "instant" });
            return;
          }
        } catch {}
        if (++tries < 15) setTimeout(poll, 2000);
        else { setPayState("paid"); setDone(true); } // webhook may be delayed; project is saved regardless
      };
      poll();
    }
  }, [searchParams]);

  const startCheckout = async (projectId) => {
    const r = await fetch(`${API}/payments/project-checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ project_id: projectId, origin_url: window.location.origin }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || "Could not start checkout — please try again.");
    }
    const d = await r.json();
    window.location.href = d.checkout_url;
  };

  const updInfo = (k) => (e) => setInfo((s) => ({ ...s, [k]: e.target.value }));
  const updDetails = (k) => (e) => setDetails((s) => ({ ...s, [k]: e.target.value }));
  const toggleService = (s) => setServices((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  // ── pricing logic ──
  const pricedItems = services.filter((s) => FIXED_PRICES[s]).map((s) => ({ name: s, ...FIXED_PRICES[s] }));
  const reviewItems = services.filter((s) => !FIXED_PRICES[s]);
  const fixedTotal = pricedItems.reduce((t, x) => t + x.price, 0);
  const depositAmt = fixedTotal / 2;
  const monthlySelected = services.includes(MONTHLY_SERVICE);
  const reviewOnly = fixedTotal === 0;

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles((cur) => [...cur, ...picked].slice(0, 10));
    e.target.value = "";
  };
  const removeFile = (i) => setFiles((cur) => cur.filter((_, x) => x !== i));

  const linkList = (list, setList, ph, testid) => (
    <div className="space-y-3">
      {list.map((v, i) => (
        <div key={i} className="flex gap-3">
          <input value={v} onChange={(e) => setList((cur) => cur.map((x, xi) => (xi === i ? e.target.value : x)))}
            className={inputCls} placeholder={ph} data-testid={`${testid}-${i}`} type="url" />
          {list.length > 1 && (
            <button type="button" onClick={() => setList((cur) => cur.filter((_, xi) => xi !== i))}
              className="text-copper-700 text-sm font-semibold shrink-0 hover:text-copper-500" data-testid={`${testid}-remove-${i}`}>Remove</button>
          )}
        </div>
      ))}
      <button type="button" onClick={() => setList((cur) => [...cur, ""])}
        className="text-copper-700 font-bold text-sm hover:text-copper-500" data-testid={`${testid}-add`}>+ Add another link</button>
    </div>
  );

  const validate = () => {
    if (!info.full_name.trim()) return "Please enter your full name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(info.email)) return "Please enter a valid email address.";
    if (!info.phone.trim()) return "Please enter your phone number.";
    if (!services.length) return "Please select at least one service.";
    if (services.includes("Other") && !otherText.trim()) return "Please explain what you need (Other is selected).";
    if (!details.create_description.trim()) return "Please tell us what you would like us to create.";
    if (terms.some((t) => !t)) return "Please check all agreement boxes before submitting.";
    return "";
  };

  const submit = async () => {
    const msg = validate();
    if (msg) { setError(msg); scrollToId("syp-payment"); return; }
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...info, services, other_text: otherText, ...details,
        work_links: workLinks.map((x) => x.trim()).filter(Boolean),
        inspiration_links: inspLinks.map((x) => x.trim()).filter(Boolean),
        inspiration_notes: inspNotes,
        payment_choice: fixedTotal >= 200 ? payment : null,
        terms,
      };
      const fd = new FormData();
      fd.append("payload", JSON.stringify(payload));
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/projects`, { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed — please try again.");
      }
      const data = await res.json();
      if (data.payment_required) {
        // fixed-price project → straight to secure card checkout for the selected amount
        await startCheckout(data.id);
        return;
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (e) {
      setError(e.message || "Submission failed — please try again.");
      scrollToId("syp-payment");
    } finally {
      setSubmitting(false);
    }
  };

  // ── confirmation screen ──
  if (done) {
    return (
      <div data-testid="syp-confirmation" className="bg-[#171210] min-h-[80vh] flex items-center">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-copper-200 to-copper-500 text-[#1C1310] mx-auto flex items-center justify-center mb-8"><Check size={30} strokeWidth={3} /></div>
          <h1 className="font-serif font-medium text-white text-4xl md:text-5xl leading-tight">Project Request<br />Received</h1>
          <p className="font-script text-copper-300 text-3xl mt-5">Thank you for choosing Toliver Signature Services.</p>
          <p className="text-white/80 mt-6 leading-relaxed">Your project request has been received. We'll review your information, files, links, and project details and contact you if anything else is needed.</p>
          <p className="text-white/60 mt-4 text-sm">A confirmation has also been sent to your email.</p>
          {payState === "processing" && (
            <p className="text-copper-300 mt-6 text-sm tracking-wide" data-testid="syp-pay-processing">Confirming your payment…</p>
          )}
          {payState === "paid" && (
            <p className="text-copper-300 mt-6 font-semibold" data-testid="syp-pay-received">Your payment was received. Thank you!</p>
          )}
          {payState === "cancelled" && (
            <div className="mt-8 border border-copper-300/40 rounded-xl p-6" data-testid="syp-pay-cancelled">
              <p className="text-white/85 text-sm">Your payment was not completed. Your project request is saved — you can finish paying securely whenever you're ready.</p>
              {pendingProject && (
                <button onClick={async () => { setPayError(""); try { await startCheckout(pendingProject); } catch (e) { setPayError(e.message || "Could not restart checkout — please email us."); } }} data-testid="syp-complete-payment" className="btn-copper mt-5">COMPLETE PAYMENT</button>
              )}
              {payError && <p className="text-red-400 text-sm mt-3" data-testid="syp-pay-error">{payError}</p>}
            </div>
          )}
          <div className="mt-10">
            <button onClick={() => navigate("/")} data-testid="syp-back-home" className="btn-copper-outline">BACK TO HOME</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="start-project-page" className="bg-[#F7F1E8] text-ink-900">

      {/* ── HERO ── */}
      <section className="bg-[#171210] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(199,147,105,0.14),transparent_55%)]" />
        <div className="relative max-w-[800px] mx-auto px-6 pt-16 pb-14 md:pt-20 md:pb-16 text-center md:text-left">
          <p className="text-[11px] uppercase tracking-[0.4em] font-semibold text-copper-300 mb-5">Already know what you need?</p>
          <h1 className="font-serif font-medium text-5xl md:text-6xl leading-tight">Start Your Project</h1>
          <p className="font-script text-copper-300 text-3xl md:text-4xl mt-4">Ready to bring your vision to life?</p>
          <p className="text-white/80 mt-5 leading-relaxed max-w-xl mx-auto md:mx-0">Tell us what you need, provide your project details, upload your files, and we'll take it from there.</p>

          <div className="flex items-center justify-between max-w-lg mx-auto md:mx-0 mt-10">
            {STEPS.map((s, i) => (
              <div key={s.n} className="flex items-center flex-1 last:flex-none">
                <button onClick={() => scrollToId(s.target)} data-testid={`step-nav-${s.n}`} className="flex flex-col items-center gap-2 group">
                  <span className={`w-8 h-8 md:w-9 md:h-9 rounded-full border flex items-center justify-center text-xs md:text-sm font-semibold transition-colors ${i === 0 ? "bg-gradient-to-br from-copper-200 to-copper-500 text-[#1C1310] border-transparent" : "border-copper-400/60 text-copper-200 group-hover:border-copper-300"}`}>{s.n}</span>
                  <span className="text-[9px] md:text-[10px] uppercase tracking-[0.08em] md:tracking-[0.12em] text-white/70 whitespace-nowrap">{s.label}</span>
                </button>
                {i < STEPS.length - 1 && <span className="flex-1 h-px bg-copper-400/30 mx-1 mb-6" />}
              </div>
            ))}
          </div>

          <p className="text-white/60 text-sm mt-8">Just need a price first?{" "}
            <button onClick={() => { navigate("/"); setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 250); }}
              className="text-copper-300 underline underline-offset-4 hover:text-copper-200" data-testid="syp-quote-link">Request a free quote instead.</button>
          </p>
        </div>
      </section>

      {/* ── 1 YOUR INFORMATION ── */}
      <section id="syp-info" data-testid="syp-info" className="py-16 md:py-20 scroll-mt-24">
        <div className="max-w-[800px] mx-auto px-6">
          <SectionHead n="1" title="Your Information" heading="Who are we working with?" />
          <div className="space-y-6">
            <div><label className={labelCls}>Full Name *</label><input data-testid="syp-name" className={inputCls} value={info.full_name} onChange={updInfo("full_name")} /></div>
            <div><label className={labelCls}>Business Name</label><input data-testid="syp-business" className={inputCls} value={info.business_name} onChange={updInfo("business_name")} /></div>
            <div><label className={labelCls}>Email Address *</label><input data-testid="syp-email" type="email" className={inputCls} value={info.email} onChange={updInfo("email")} /></div>
            <div><label className={labelCls}>Phone Number *</label><input data-testid="syp-phone" type="tel" className={inputCls} value={info.phone} onChange={updInfo("phone")} /></div>
            <div><label className={labelCls}>Website</label><p className={helpCls}>If you have one</p><input data-testid="syp-website" className={inputCls} value={info.website} onChange={updInfo("website")} /></div>
            <div><label className={labelCls}>Social Media Links</label><p className={helpCls}>Facebook, Instagram, TikTok, or others</p><input data-testid="syp-socials" className={inputCls} value={info.social_links} onChange={updInfo("social_links")} /></div>
          </div>
        </div>
      </section>

      {/* ── 2 WHAT DO YOU NEED? ── */}
      <section id="syp-services" data-testid="syp-services" className="py-16 md:py-20 bg-[#EFE5D6] scroll-mt-24">
        <div className="max-w-[800px] mx-auto px-6">
          <SectionHead n="2" title="What Do You Need?" heading="Choose everything that applies." />
          <p className="text-ink-800/70 -mt-4 mb-8">Select as many services as you need.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SERVICE_OPTIONS.map((s) => {
              const on = services.includes(s);
              const wide = s === MONTHLY_SERVICE;
              return (
                <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`syp-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`flex items-center gap-3.5 bg-white rounded-xl border-2 px-5 py-4 text-left transition-all ${wide ? "sm:col-span-2" : ""} ${on ? "border-copper-500 shadow-[0_4px_18px_rgba(180,122,90,0.25)]" : "border-copper/15 hover:border-copper/40"}`}>
                  <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 border-2 ${on ? "bg-ink-900 border-ink-900" : "border-ink-800/30"}`}>
                    {on && <Check size={13} className="text-white" strokeWidth={3.5} />}
                  </span>
                  <span className="font-medium text-[15px]">{s}</span>
                  {FIXED_PRICES[s] && <span className="ml-auto font-serif font-bold text-copper-600 whitespace-nowrap">${FIXED_PRICES[s].price}</span>}
                </button>
              );
            })}
          </div>
          {services.includes("Other") && (
            <div className="mt-6" data-testid="syp-other-wrap">
              <label className={labelCls}>Please explain what you need *</label>
              <textarea rows={3} className={`${inputCls} resize-none`} value={otherText} onChange={(e) => setOtherText(e.target.value)} data-testid="syp-other-text" />
            </div>
          )}
        </div>
      </section>

      {/* ── 3 PROJECT DETAILS ── */}
      <section id="syp-details" data-testid="syp-details" className="py-16 md:py-20 scroll-mt-24">
        <div className="max-w-[800px] mx-auto px-6">
          <SectionHead n="3" title="Project Details" heading="Tell us about your project." />
          <div className="space-y-6">
            {DETAIL_FIELDS.map((f) => (
              <div key={f.key}>
                <label className={labelCls}>{f.label}{f.required ? " *" : ""}</label>
                <textarea rows={f.key === "create_description" ? 4 : 3} className={`${inputCls} resize-none`} value={details[f.key]} onChange={updDetails(f.key)} data-testid={`syp-${f.key.replace(/_/g, "-")}`} />
              </div>
            ))}
            <div>
              <label className={labelCls}>Requested completion date</label>
              <input type="date" className={inputCls} value={details.completion_date} onChange={updDetails("completion_date")} data-testid="syp-completion-date" />
            </div>
            <div>
              <label className={labelCls}>Anything else we should know?</label>
              <textarea rows={3} className={`${inputCls} resize-none`} value={details.anything_else} onChange={updDetails("anything_else")} data-testid="syp-anything-else" />
            </div>
          </div>
        </div>
      </section>

      {/* ── 4 FILES & LINKS ── */}
      <section id="syp-files" data-testid="syp-files" className="py-16 md:py-20 bg-[#EFE5D6] scroll-mt-24">
        <div className="max-w-[800px] mx-auto px-6">
          <SectionHead n="4" title="Files & Links" heading="Share what we should see." />

          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-copper-700 mb-4">A · Upload Your Project Files</p>
          <div className="border-2 border-dashed border-copper-400/60 rounded-xl bg-white/60 px-6 py-10 text-center">
            <Upload size={30} className="mx-auto text-copper-600 mb-4" />
            <p className="font-bold text-lg">Upload your files</p>
            <p className="text-sm text-ink-800/70 mt-2 max-w-md mx-auto">Logos · Photos · PDFs · Word documents · Menus · Price lists · Existing flyers · Brand guides · Business documents · Screenshots · Product photos · Existing marketing materials · Other project files</p>
            <button type="button" onClick={() => fileInput.current?.click()} data-testid="syp-choose-files"
              className="mt-6 border-2 border-ink-900 text-ink-900 uppercase tracking-[0.18em] font-bold text-xs px-8 py-3.5 rounded-lg hover:bg-ink-900 hover:text-white transition-colors">Choose Files</button>
            <p className="text-sm text-ink-800/60 mt-3">You can select multiple files.</p>
            <input ref={fileInput} type="file" multiple className="hidden" onChange={addFiles} data-testid="syp-file-input"
              accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.svg,.ai,.eps" />
          </div>
          {files.length > 0 && (
            <ul className="mt-4 space-y-2.5" data-testid="syp-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center justify-between bg-white rounded-lg border border-copper/15 px-4 py-3">
                  <span className="text-sm truncate mr-4">{f.name}</span>
                  <button type="button" onClick={() => removeFile(i)} className="text-copper-700 text-sm font-semibold hover:text-copper-500 shrink-0" data-testid={`syp-file-remove-${i}`}>Remove</button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-sm text-ink-800/70 mt-4">Please upload the highest-quality original files available.<br />Videos and very large files should be shared as links in the next section.</p>

          <div className="border-t border-copper/20 my-10" />
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-copper-700 mb-4">B · Files For Us To Work With</p>
          <p className="text-ink-800/80 mb-2">Have videos, large files, or folders you want us to work with? Paste your shareable links below.</p>
          <p className={helpCls}>Google Drive, Dropbox, OneDrive, WeTransfer, or YouTube links</p>
          {linkList(workLinks, setWorkLinks, "Paste a shareable link", "syp-work-link")}

          <div className="border-t border-copper/20 my-10" />
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-copper-700 mb-4">C · Inspiration &amp; Examples</p>
          <p className="text-ink-800/80 mb-5">Share designs, websites, or posts you like. These are references only, not files we'll use in your project.</p>
          <label className={labelCls}>Inspiration / Example Links</label>
          {linkList(inspLinks, setInspLinks, "Paste an example link", "syp-insp-link")}
          <div className="mt-6">
            <label className={labelCls}>What do you like about these examples?</label>
            <textarea rows={3} className={`${inputCls} resize-none`} value={inspNotes} onChange={(e) => setInspNotes(e.target.value)} data-testid="syp-insp-notes" />
          </div>
        </div>
      </section>

      {/* ── 5 REVIEW ── */}
      <section id="syp-review" data-testid="syp-review" className="py-16 md:py-20 scroll-mt-24">
        <div className="max-w-[800px] mx-auto px-6">
          <SectionHead n="5" title="Review" heading="Review your project." />
          <p className="text-ink-800/70 -mt-4 mb-8">Check everything below. Tap Edit to change any section before you submit.</p>

          <div className="space-y-4">
            <ReviewCard title="Your Information" editTarget="syp-info" testid="syp-review-info">
              <ReviewRow k="Name" v={info.full_name} />
              {info.business_name && <ReviewRow k="Business" v={info.business_name} />}
              <ReviewRow k="Email" v={info.email} />
              <ReviewRow k="Phone" v={info.phone} />
              {info.website && <ReviewRow k="Website" v={info.website} />}
              {info.social_links && <ReviewRow k="Socials" v={info.social_links} />}
            </ReviewCard>

            <ReviewCard title="Services Selected" editTarget="syp-services" testid="syp-review-services">
              {services.length ? services.map((s) => <p key={s} className="text-[15px] py-0.5">{s}</p>) : <p className="text-ink-800/50 text-sm">None selected yet</p>}
            </ReviewCard>

            <ReviewCard title="Project Details" editTarget="syp-details" testid="syp-review-details">
              <ReviewRow k="Create" v={details.create_description} />
              {details.completion_date && <ReviewRow k="Completion date" v={details.completion_date} />}
            </ReviewCard>

            <ReviewCard title="Uploaded Files" editTarget="syp-files" testid="syp-review-files">
              {files.length ? files.map((f, i) => <p key={i} className="text-[15px] py-0.5">{f.name}</p>) : <p className="text-ink-800/50 text-sm">No files uploaded</p>}
            </ReviewCard>

            <ReviewCard title="Files & Video Links" editTarget="syp-files" testid="syp-review-links">
              <p className="text-[15px]">{workLinks.filter((x) => x.trim()).length} link{workLinks.filter((x) => x.trim()).length === 1 ? "" : "s"}</p>
            </ReviewCard>

            <ReviewCard title="Inspiration Links" editTarget="syp-files" testid="syp-review-insp">
              <p className="text-[15px]">{inspLinks.filter((x) => x.trim()).length} link{inspLinks.filter((x) => x.trim()).length === 1 ? "" : "s"}</p>
            </ReviewCard>

            {/* PROJECT PRICING */}
            <div className="bg-[#1B1411] rounded-xl p-6 md:p-7" data-testid="syp-review-pricing">
              <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-copper-300 mb-4">Project Pricing</p>
              {services.length === 0 ? (
                <p className="text-white/60 text-sm">Select your services above to see your project pricing.</p>
              ) : pricedItems.length > 0 ? (
                <>
                  <div className="space-y-2.5">
                    {pricedItems.map((p) => (
                      <div key={p.name} className="flex justify-between gap-4 text-[15px]">
                        <span className="text-white/90">{p.label}</span>
                        <span className="text-white font-semibold whitespace-nowrap">{fmt(p.price)}</span>
                      </div>
                    ))}
                    {reviewItems.map((s) => (
                      <div key={s} className="flex justify-between gap-4 text-[15px]">
                        <span className="text-white/90">{s}</span>
                        <span className="text-copper-300 font-bold text-xs tracking-wider whitespace-nowrap pt-0.5">REVIEW REQUIRED</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-white/15 mt-5 pt-4 flex justify-between items-baseline">
                    <span className="text-white font-bold">Total{reviewItems.length ? " (fixed-price items)" : ""}</span>
                    <span className="font-serif text-copper-300 text-3xl">{fmt(fixedTotal)}</span>
                  </div>
                </>
              ) : (
                <p className="text-copper-300 font-bold tracking-wider text-sm">REVIEW REQUIRED</p>
              )}
            </div>

            {reviewItems.length > 0 && (
              <div className="border border-copper-400/50 bg-copper-50 rounded-xl p-5" data-testid="syp-review-note">
                <p className="text-copper-700 font-bold text-xs tracking-[0.25em] uppercase">Review Required</p>
                <p className="text-sm text-ink-800/85 mt-2">Your project details will be reviewed before your final price is confirmed.</p>
                <p className="text-sm text-ink-800/85 mt-1">No payment is collected until your final price is approved.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── 6 PAYMENT ── */}
      <section id="syp-payment" data-testid="syp-payment" className="py-16 md:py-20 bg-[#171210] text-white scroll-mt-24">
        <div className="max-w-[800px] mx-auto px-6">
          <SectionHead n="6" title="Payment" heading="Choose how to pay" dark />

          {services.length === 0 ? (
            <div className="border border-white/15 bg-[#1E1613] rounded-xl p-7" data-testid="syp-payment-empty">
              <p className="text-white/60 text-[15px]">Your payment options will appear here once you choose your services above.</p>
            </div>
          ) : reviewOnly ? (
            <div className="border border-copper-300/40 bg-[#1E1613] rounded-xl p-7" data-testid="syp-payment-review">
              <p className="text-copper-300 font-bold text-sm tracking-[0.25em] uppercase">Review Required</p>
              <p className="text-white/80 mt-3 text-[15px]">Your project details will be reviewed before your final price is confirmed.</p>
              <p className="text-white/80 mt-2 text-[15px]">No payment is collected until your final price is approved.</p>
              {monthlySelected && <p className="text-white/60 mt-3 text-sm">Monthly Social Media Management is set up as recurring monthly billing after your plan is confirmed.</p>}
            </div>
          ) : fixedTotal < 200 ? (
            <div className="border-2 border-copper-400 bg-[#1E1613] rounded-xl p-7" data-testid="syp-payment-full">
              <p className="font-bold text-lg">Full Payment Required</p>
              <p className="font-serif text-copper-300 text-4xl mt-3">{fmt(fixedTotal)}</p>
              <p className="text-white/60 text-sm mt-2">Projects under $200 are paid in full upfront.</p>
              {reviewItems.length > 0 && <p className="text-white/60 text-sm mt-3">Items marked REVIEW REQUIRED will be priced and approved separately before payment.</p>}
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="syp-payment-options">
                <button type="button" onClick={() => setPayment("deposit")} data-testid="syp-pay-deposit"
                  className={`text-left rounded-xl border-2 p-6 transition-all ${payment === "deposit" ? "border-copper-400 bg-[#241a15]" : "border-white/15 bg-[#1E1613] hover:border-copper/40"}`}>
                  <span className={`inline-block w-4 h-4 rounded-full border-2 mr-2 align-middle ${payment === "deposit" ? "border-copper-300 bg-copper-300" : "border-white/40"}`} />
                  <span className="font-bold">50% Deposit</span>
                  <p className="font-serif text-copper-300 text-4xl mt-4">{fmt(depositAmt)}</p>
                  <p className="text-white/60 text-sm mt-2">Balance of {fmt(depositAmt)} due before final files</p>
                </button>
                <button type="button" onClick={() => setPayment("full")} data-testid="syp-pay-full"
                  className={`text-left rounded-xl border-2 p-6 transition-all ${payment === "full" ? "border-copper-400 bg-[#241a15]" : "border-white/15 bg-[#1E1613] hover:border-copper/40"}`}>
                  <span className={`inline-block w-4 h-4 rounded-full border-2 mr-2 align-middle ${payment === "full" ? "border-copper-300 bg-copper-300" : "border-white/40"}`} />
                  <span className="font-bold">Pay in Full</span>
                  <p className="font-serif text-copper-300 text-4xl mt-4">{fmt(fixedTotal)}</p>
                  <p className="text-white/60 text-sm mt-2">Paid once, nothing due later</p>
                </button>
              </div>
              {reviewItems.length > 0 && <p className="text-white/60 text-sm mt-4">Items marked REVIEW REQUIRED will be priced and approved separately before payment.</p>}
              {monthlySelected && <p className="text-white/60 text-sm mt-2">Monthly Social Media Management is set up as recurring monthly billing after your plan is confirmed.</p>}
            </>
          )}

          <p className="text-white/60 text-sm mt-8 leading-relaxed">
            Projects under $200 are paid in full upfront.<br />
            Eligible fixed-price projects over $200 may be started with a 50% deposit or paid in full.<br />
            Services requiring scope review will be priced and approved before payment.
          </p>

          <div className="mt-9 space-y-5">
            {TERMS.map((t, i) => (
              <label key={i} className="flex items-start gap-4 cursor-pointer" data-testid={`syp-term-${i}`}>
                <input type="checkbox" checked={terms[i]} onChange={() => setTerms((cur) => cur.map((x, xi) => (xi === i ? !x : x)))}
                  className="mt-1 w-5 h-5 shrink-0 accent-[#C88F66] cursor-pointer" data-testid={`syp-term-check-${i}`} />
                <span className="text-[15px] text-white/85">
                  {i === TERMS.length - 1 ? (
                    <>I agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="syp-terms-link" className="text-copper-300 underline underline-offset-2 hover:text-copper-200">project and payment terms</Link>. *</>
                  ) : (<>{t} *</>)}
                </span>
              </label>
            ))}
          </div>

          {error && <p className="text-red-400 text-sm mt-6" data-testid="syp-error">{error}</p>}

          <button onClick={submit} disabled={submitting} data-testid="syp-submit"
            className="btn-copper w-full mt-9 !py-5 !text-sm disabled:opacity-60">
            {submitting ? "PLEASE WAIT…" : "SUBMIT MY PROJECT"}
          </button>
          <p className="text-white/50 text-xs text-center mt-3">Fixed-price projects continue to a secure card checkout after submitting.</p>
          <p className="text-white/50 text-xs text-center mt-2">By submitting this form, you agree to our <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-copper-300 hover:text-copper-200">Terms &amp; Policies</Link> and <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="syp-privacy-link" className="underline underline-offset-2 text-copper-300 hover:text-copper-200">Privacy Policy</Link>.</p>

          <p className="text-white/60 text-sm text-center mt-7">
            Questions? Email <a href="mailto:toliversignatureservices@gmail.com" className="text-copper-300 hover:text-copper-200">toliversignatureservices@gmail.com</a> or call <a href="tel:6629126357" className="text-copper-300 hover:text-copper-200">662-912-6357</a>.
          </p>
        </div>
      </section>

      {/* ── PAGE FOOTER ── */}
      <footer className="bg-black text-white py-14 text-center">
        <img src={LOGO} alt="Toliver Signature Services" className="h-24 w-24 rounded-full ring-1 ring-copper/40 object-cover mx-auto" data-testid="syp-footer-logo" />
        <p className="font-script text-copper-300 text-3xl mt-5">Your Vision. Our Signature.</p>
        <p className="text-white/70 text-sm mt-4">
          <a href="tel:6629126357" className="hover:text-copper-300">662-912-6357</a>
          <span className="mx-2 text-white/40">·</span>
          <a href="mailto:toliversignatureservices@gmail.com" className="hover:text-copper-300">toliversignatureservices@gmail.com</a>
        </p>
        <p className="text-white/40 text-xs mt-8">
          © {new Date().getFullYear()} Toliver Signature Services LLC ·{" "}
          <Link to="/terms" className="underline underline-offset-2 hover:text-copper-300">Terms &amp; Policies</Link> ·{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-copper-300">Privacy Policy</Link>
        </p>
      </footer>
    </div>
  );
}

function ReviewCard({ title, editTarget, testid, children }) {
  return (
    <div className="bg-white rounded-xl border border-copper/15 p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-copper-700">{title}</p>
        <button type="button" onClick={() => scrollToId(editTarget)} className="text-copper-700 text-sm font-semibold underline underline-offset-4 hover:text-copper-500" data-testid={`${testid}-edit`}>Edit</button>
      </div>
      {children}
    </div>
  );
}

function ReviewRow({ k, v }) {
  return (
    <div className="flex justify-between gap-6 py-1 text-[15px]">
      <span className="text-ink-800/60">{k}</span>
      <span className="text-right font-medium break-words max-w-[60%]">{v}</span>
    </div>
  );
}
